import { useLayoutEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AppHeader from '../components/Layout/AppHeader';
import { Sidebar } from '../components';
import {
  buildMenu,
  deriveActiveKey,
  deriveParentKeys,
} from '../pages/Agenda/buildMenu';
import { SidebarDrawerProvider, useSidebarDrawer } from '../hooks/useSidebarDrawer';
import { useAuth } from '../auth/AuthContext';
import { needsOnboarding } from '../auth/onboarding';
import { useTenantRole } from '../hooks/useTenantRole';
import {
  isSchedulingIncluded,
  resolveSubscriptionNotice,
  PLAN_UPGRADE_URL,
} from '../utils/planCapabilities';
import styles from './AppLayout.module.css';

/**
 * Copy por tipo de aviso de assinatura. Separada do predicado de propósito: o
 * predicado (`resolveSubscriptionNotice`, em `utils/planCapabilities.js`) é puro
 * e testável sem React; o texto é UI e vive na UI.
 *
 * `tone` mapeia para a classe do CSS Module — `warning` para o que ainda dá
 * tempo de resolver, `danger` para o que já está bloqueando. Nenhum dos dois é
 * "erro de sistema": todo aviso termina numa saída clara (o CTA de planos).
 */
const SUBSCRIPTION_NOTICE_COPY = {
  TRIAL_ENDING: (daysLeft) => ({
    tone: 'warning',
    text:
      daysLeft <= 1
        ? 'Seu período de teste termina em menos de 1 dia.'
        : `Seu período de teste termina em ${daysLeft} dias.`,
    cta: 'Escolher um plano',
  }),
  TRIAL_EXPIRED: () => ({
    tone: 'danger',
    text:
      'Seu período de teste terminou e o atendimento automático está pausado.',
    cta: 'Escolher um plano',
  }),
  OVERDUE: () => ({
    tone: 'danger',
    text:
      'Sua assinatura está com pagamento em atraso e o atendimento automático está pausado.',
    cta: 'Regularizar pagamento',
  }),
  CANCELED: () => ({
    tone: 'danger',
    text: 'Sua assinatura foi cancelada e o atendimento automático está pausado.',
    cta: 'Reativar assinatura',
  }),
};

/**
 * Aviso de negócio incompleto — tenant existe, mas sem `segment`.
 *
 * É o caso do signup por e-mail/senha: `authController.ts:168-177` cria o
 * tenant com `name: "Minha Empresa"` e sem segmento. Essa gente TEM produto
 * funcionando; o segmento só afeta a copy, o filtro do menu e o tom do bot.
 * Por isso aqui é banner e não cadeado — o cadeado (`/onboarding`) fica
 * reservado a quem não tem tenant nenhum. Ver `auth/onboarding.js`.
 *
 * `tone: 'info'` de propósito: não é urgência nem falha, é uma pendência de
 * cadastro. O CTA aponta para `/agenda?setup=true`, o gatilho explícito que
 * abre o wizard — o mesmo que a Agenda já tratava antes de o modal deixar de
 * abrir sozinho.
 */
const ONBOARDING_NOTICE_COPY = {
  tone: 'info',
  text: 'Falta dizer que tipo de negócio você atende — sem isso a IA responde de forma genérica.',
  cta: 'Completar cadastro',
  href: '/agenda?setup=true',
};

/**
 * Conteúdo interno do AppLayout — pode acessar o contexto do drawer.
 *
 * A Sidebar é GLOBAL: vive aqui (não mais opt-in por página), então aparece em
 * todas as rotas autenticadas e vira drawer no mobile via `useSidebarDrawer`.
 * O item ativo é derivado da ROTA (`useLocation`), casando pelo `link` mais
 * específico de cada item — sem `activeKey` manual por página.
 */
function AppLayoutInner({ children }) {
  const { drawerOpen, openDrawer, closeDrawer } = useSidebarDrawer();
  const navigate = useNavigate();
  const location = useLocation();

  // Dados do tenant só para o menu (nome da empresa + filtro por segmento).
  // Fonte única: AuthContext já busca `/auth/me` uma vez (no mount + refreshUser)
  // e distribui via `user` — cujo shape (`.tenant.name`/`.tenant.segment`) é o
  // mesmo que o buildMenu consome. Evita refetch de tenant a CADA navegação
  // (cada rota autenticada remonta este AppLayout). buildMenu ainda tem fallback
  // de segmento em localStorage, então o menu renderiza estável antes de o user
  // hidratar.
  const { user } = useAuth();
  const businessData = user;

  // Plano inclui a Central de Atendimento (CRM)? Fonte: `/auth/me` →
  // `tenant.plan.crmIncluded`. Gateia itens `requiresCrm` do menu (ex.:
  // "Setores"). O item "Conversas" (inbox base) NÃO é gateado — fica visível ao
  // Básico. Enquanto o user não hidrata, cai em false (nega por omissão).
  const crmIncluded = !!user?.tenant?.plan?.crmIncluded;

  // Plano inclui agendamento? Fonte: `/auth/me` → `tenant.plan.schedulingIncluded`
  // (ADR-0026). ⚠️ FAIL-SAFE INVERTIDO em relação ao `crmIncluded` acima: só
  // `false` EXPLÍCITO tranca. Campo ausente do payload, plano nulo ou user ainda
  // não hidratado → agenda liberada. Não troque por `!!...` — isso sumiria com o
  // cadeado... e, pior, com nada: o menu *não esconde* itens de agenda, então o
  // erro apareceria como cadeado em cliente pagante. Mesma regra de
  // `usePlanFeature("scheduling")`.
  const schedulingIncluded = isSchedulingIncluded(user?.tenant?.plan);

  // Papel do usuário no tenant (OWNER | ADMIN | MEMBER) para gatear itens de
  // menu `requiresRole` (ex.: "Setores" só para OWNER/ADMIN). Cacheado em
  // módulo pelo hook — não refaz fetch a cada navegação.
  const { role } = useTenantRole();

  // Estado da assinatura → banner no topo do conteúdo. Fonte: `/auth/me` →
  // `tenant.subscriptionStatus` / `tenant.trialExpiration`.
  //
  // Existe porque, quando o trial vence (ou a assinatura fica OVERDUE/CANCELED),
  // o bot do WhatsApp fica MUDO e nada avisa o dono: ele descobre pelo silêncio.
  // O estado até aparecia em /subscription/plans e /agenda/settings, mas só para
  // quem foi olhar — telas que ninguém visita justamente quando o produto parou.
  //
  // ⚠️ FAIL-SAFE `null` (nenhum banner) e é o INVERSO do `schedulingIncluded`
  // acima. Lá, "não sei" libera; aqui, "não sei" CALA. Errar para "mostra"
  // acusa de inadimplente quem está em dia. O porquê completo mora em
  // `utils/planCapabilities.js` — não troque por um `default` que avisa.
  const subscriptionNotice = resolveSubscriptionNotice(user?.tenant);
  const subscriptionCopy = subscriptionNotice
    ? SUBSCRIPTION_NOTICE_COPY[subscriptionNotice.kind](
        subscriptionNotice.daysLeft,
      )
    : null;

  // UM banner por vez, e o de assinatura ganha. Dois empilhados empurrariam o
  // conteúdo para baixo em toda rota autenticada e, pior, diluiriam o aviso que
  // importa: o de cobrança é dinheiro parado e o produto já mudo; o de cadastro
  // é uma pendência de copy que espera desde o signup e pode esperar mais um
  // dia. Quem resolve a cobrança volta a ver o de cadastro na navegação
  // seguinte — nada se perde, só se enfileira.
  const noticeCopy =
    subscriptionCopy ??
    (needsOnboarding(user) ? ONBOARDING_NOTICE_COPY : null);

  // Marca o <body> como área autenticada enquanto o AppLayout está montado.
  // A regra `:global(body.nx-app-shell)` (AppLayout.module.css) zera o
  // padding/margin do body — necessário porque vários CSS Modules legados
  // (services/settings/Dashboard/Footer/Header) vazam `body { padding: 2vh 5vw }`
  // global, criando uma moldura navy que o surface do shell não conseguia cobrir.
  // useLayoutEffect (não useEffect) roda ANTES do paint, evitando flash navy ao
  // navegar entre rotas autenticadas (cada rota remonta o AppLayout). Idempotente:
  // add/remove de classList são no-op se já no estado esperado.
  useLayoutEffect(() => {
    document.body.classList.add('nx-app-shell');
    return () => document.body.classList.remove('nx-app-shell');
  }, []);

  // Menu global (variant "agenda" → navegação real de páginas). As ações de
  // conexão (WhatsApp/Instagram) vivem na tela /agenda, então redirecionam
  // para lá com um intent via query param, que a Agenda dispara ao montar.
  const menuItems = useMemo(
    () =>
      buildMenu({
        businessData,
        navigate,
        variant: 'agenda',
        role,
        crmIncluded,
        schedulingIncluded,
        handlers: {
          handleConnectWhatsApp: () => navigate('/agenda?connect=whatsapp'),
          handleConnectWhatsAppCoexistence: () =>
            navigate('/agenda?connect=coexistence'),
          handleConnectInstagram: () => navigate('/agenda?connect=instagram'),
        },
      }),
    [businessData, navigate, role, crmIncluded, schedulingIncluded],
  );

  const activeKey = useMemo(
    () => deriveActiveKey(location.pathname, menuItems),
    [location.pathname, menuItems],
  );

  // Grupo(s) do item ativo, auto-abertos ao MONTAR a sidebar (via
  // defaultOpenKeys). Sem isso, um item aninhado ativo (ex.: Bloqueios,
  // Listagem de clientes, sub-rotas de Profissionais) só ganha destaque
  // depois que o usuário expande o grupo manualmente. Rotas top-level não
  // têm pai → []. Como defaultOpenKeys alimenta o estado INICIAL da Sidebar,
  // isto não interfere no toggle manual posterior.
  const defaultOpenKeys = useMemo(
    () => deriveParentKeys(activeKey, menuItems),
    [activeKey, menuItems],
  );

  return (
    <>
      <AppHeader onMenuClick={openDrawer} menuExpanded={drawerOpen} />
      <div className={styles.shell}>
        <Sidebar
          menuItems={menuItems}
          activeKey={activeKey}
          defaultOpenKeys={defaultOpenKeys}
          drawerOpen={drawerOpen}
          onDrawerClose={closeDrawer}
        />
        <main className={styles.main}>
          {noticeCopy && (
            // `role="status"` (polite), não `role="alert"`: o AppLayout remonta
            // a cada navegação, e um alert assertivo interromperia o leitor de
            // tela em toda troca de rota. O CTA é um `Link` do router — navega
            // sem recarregar a SPA — e aponta para `PLAN_UPGRADE_URL`, o destino
            // canônico de upgrade (invariante 4), o mesmo de `usePlanFeature` e
            // do toast de `notifyPlanUpgrade`.
            <div
              className={`${styles.subscriptionBanner} ${styles[noticeCopy.tone]}`}
              role="status"
            >
              <p className={styles.subscriptionBannerText}>{noticeCopy.text}</p>
              <Link
                to={noticeCopy.href ?? PLAN_UPGRADE_URL}
                className={styles.subscriptionBannerCta}
              >
                {noticeCopy.cta}
              </Link>
            </div>
          )}
          {children}
        </main>
      </div>
    </>
  );
}

/**
 * AppLayout — layout da área autenticada.
 * AppHeader fixo no topo + Sidebar global à esquerda + <main> com surface
 * clara (`--nx-bg`) cobrindo o gradiente navy do body em todas as telas.
 */
export function AppLayout({ children }) {
  return (
    <SidebarDrawerProvider>
      <AppLayoutInner>{children}</AppLayoutInner>
    </SidebarDrawerProvider>
  );
}
